import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { heapSignOut } from "@newsela/snitch";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import evaluateRestQuery from "@/remoteState/evaluateRestQuery";
import useLazyRestQuery from "@/remoteState/useLazyRestQuery";
import useRestMutation from "@/remoteState/useRestMutation";
import authenticatedServerSideRequestConfig from "@/utils/authenticatedServerSideRequestConfig";
import useIsMounted from "@/utils/useIsMounted";
import isRunningOnClient from "@/utils/isRunningOnClient";
import getQueryClient, {
  SESSION_STORAGE_OFFLINE_CACHE_KEY,
} from "@/utils/getQueryClient/getQueryClient";

const UserQueryContext = createContext({ data: null });
const UserMethodsContext = createContext({});
const UserCEContext = createContext();

const propTypes = {
  userQueryInitialValue: PropTypes.shape({
    data: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.instanceOf(Error),
  }),
  children: PropTypes.node,
};

const getMeUrl = `${MONOLITH_ORIGIN}/api/v2/user/me/`;

export const UserProvider = ({
  userQueryInitialValue = {
    data: undefined,
    loading: true,
    error: undefined,
  },
  children,
}) => {
  // check for early access
  const router = useRouter();
  let hasCE;

  if (
    router.query && router.query.newsela_ce && router.query.newsela_ce ? 1 : 0
  ) {
    hasCE = router.query.newsela_ce ? 1 : 0;
  }

  const signOutRequest = useMemo(() => {
    return new Request(`${MONOLITH_ORIGIN}/api/v2/session/`, {
      method: "DELETE",
    });
  }, []);
  const [signOut] = useRestMutation(signOutRequest);

  const [getMe] = useLazyRestQuery(getMeUrl);

  const [userData, setUserData] = useState({
    ...userQueryInitialValue,
    loading: true,
  });

  const signOutAndRefreshMe = useCallback(async () => {
    await signOut();
    heapSignOut();
    setUserData({ ...userData, loading: true });

    // Clear the query cache from 🏝️ TansTack Query as well
    const queryClient = getQueryClient();
    queryClient.clear();
    queryClient.removeQueries({ queryKey: ["user"] });
    if (isRunningOnClient()) {
      window.sessionStorage.removeItem(SESSION_STORAGE_OFFLINE_CACHE_KEY);
    }

    return await getMe().then((responseUserData) => {
      setUserData({ ...responseUserData, loading: false });
    });
  }, [signOut, getMe]);

  const refreshMe = useCallback(
    async (getReducedShape = false) => {
      const reducedUserRequest =
        getMeUrl + "?include_licenses=false&include_schools=false";
      setUserData({ ...userData, loading: true });
      const responseUserData = await (getReducedShape
        ? evaluateRestQuery(reducedUserRequest)
        : getMe());
      setUserData({ ...responseUserData, loading: false });
      return responseUserData;
    },
    [getMe],
  );

  const isMounted = useIsMounted();

  useEffect(() => {
    // Don't load active user if this is the first mount and we already have an
    // authenticated user from the initial props. Otherwise, reload if the get
    // me request has changed, or if the initial user is not authenticated.
    if (!isMounted() && userData.data?.is_authenticated) {
      setUserData({ ...userData, loading: false });
      return;
    }
    getMe().then((responseUserData) => {
      setUserData({ ...responseUserData, loading: false });
    });
  }, [getMe]);

  const methods = useMemo(
    () => ({ signOut: signOutAndRefreshMe, refreshMe }),
    [signOutAndRefreshMe, refreshMe],
  );

  return (
    <UserCEContext.Provider value={hasCE}>
      <UserQueryContext.Provider value={userData}>
        <UserMethodsContext.Provider value={methods}>
          {children}
        </UserMethodsContext.Provider>
      </UserQueryContext.Provider>
    </UserCEContext.Provider>
  );
};

UserProvider.propTypes = propTypes;

UserProvider.__getInitialProps = async ({ ctx, getReducedUserShape }) => {
  const userMeQueryParam = new URLSearchParams();
  if (getReducedUserShape) {
    userMeQueryParam.append("include_licenses", "false");
    userMeQueryParam.append("include_schools", "false");
  }
  const request = new Request(
    getMeUrl + `?${userMeQueryParam.toString()}`,
    authenticatedServerSideRequestConfig(ctx),
  );
  const userQueryInitialValue = await evaluateRestQuery(request);
  return { userQueryInitialValue };
};

const useUser = () => {
  return useContext(UserQueryContext);
};

/**
 * @deprecated Use V3 instead
 */
export const useSignOut = () => {
  return useContext(UserMethodsContext).signOut;
};

/**
 * @deprecated Use V3 instead
 */
export const useRefreshMe = () => {
  return useContext(UserMethodsContext).refreshMe;
};

export { UserCEContext };
export default useUser;
